import type { ITrackingError, ITrackerInstanceV2 } from '@zendesk-ui/tracking'

import config, { embeddedRuntime, hasZendeskProxy } from '@/config'
import captureException from '@/utils/error'

const PRODUCT = 'qa'

// True only when the app runs inside a Zendesk-proxied shell and outside the embedded
// extension iframe. The pigateway endpoint is only reachable from within the Zendesk
// shell, so gating on hasZendeskProxy avoids noisy 4xx in standalone deployments.
const isTrackingEnabled = () => hasZendeskProxy && !embeddedRuntime()

// debug mode: log to console instead of sending network requests outside production.
// Controlled by SENTRY_ENV so the same guard used by Sentry also gates tracking verbosity.
const isDebugMode = () => !['production', 'prod'].includes((config.sentryEnv ?? '').toLowerCase())

interface TrackEventOptions {
  action?: 'click' | 'submit' | 'blur' | 'focus' | 'keydown' | 'play' | 'pause' | 'page_element_load'
}

// Tracker singleton — created once during initProductTracking(), null otherwise.
let tracker: ITrackerInstanceV2 | null = null
// Destroy handle returned by enableAutoTracking
let destroyAutoTracking: (() => void) | null = null
let initPromise: Promise<void> | null = null

/**
 * Initialise product-level tracking. Call once at app bootstrap after mount.
 * No-ops when:
 *   - running inside the embedded extension iframe
 *   - running in a non-Zendesk-proxied deployment
 */
export function initProductTracking(): Promise<void> {
  if (!isTrackingEnabled() || tracker) return Promise.resolve()
  // Guard against double-init (HMR, tests)
  if (initPromise) return initPromise

  initPromise = (async () => {
    const debug = isDebugMode()

    try {
      const { createTracker, enableAutoTracking } = await import('@zendesk-ui/tracking')
      const onError = (label: string) => (error: ITrackingError) => console.warn(`[tracking] ${label}`, error)

      tracker = createTracker({
        product: PRODUCT,
        newTracker: true,
        autoTrack: true,
        debug,
        onError: onError('event delivery failed'),
      })

      destroyAutoTracking = enableAutoTracking({
        product: PRODUCT,
        trackLoad: true,
        trackRoutes: true,
        trackUnload: true,
        debug,
        onError: onError('page-view delivery failed'),
      })
    } catch (error) {
      destroyProductTracking()
      console.warn('[tracking] failed to initialize product tracking', error)
      captureException(error)
    }
  })()

  return initPromise
}

/**
 * Destroy the tracker and page-view listener. Useful for HMR cleanup.
 */
export function destroyProductTracking(): void {
  destroyAutoTracking?.()
  tracker?.destroy()
  destroyAutoTracking = null
  tracker = null
  initPromise = null
}

/**
 * Send an explicit tracking event. No-ops when tracker has not been initialised
 * (e.g. standalone / extension / test environments).
 *
 * @param eventName  Business action name, e.g. 'ai-insight-deleted'
 * @param subFeatureId  Numeric or string sub-feature identifier from `@/tracking/features/*`
 * @param options  Optional overrides (action defaults to 'click')
 */
export function trackEvent(eventName: string, subFeatureId: number | string, options: TrackEventOptions = {}): void {
  if (!tracker) return
  tracker.track({
    eventName,
    subFeatureId: String(subFeatureId),
    action: options.action ?? 'click',
  })
}
